import { Option, Select } from "@cca/components";
import type { LearningModuleItemDto } from "@core/types";
import { useNavigate, useParams } from "react-router-dom";

export type DayBreadcrumbsProps = {
  learnerName?: string;
  learners?: Option[];
  unit?: LearningModuleItemDto;
  topic?: LearningModuleItemDto;
  day?: LearningModuleItemDto;
};

function formatOrderedItem(
  label: string,
  number: number,
  title: string,
): string {
  return `${label} ${number}: ${title}`;
}

export function formatUnit(number: number, title: string): string {
  return formatOrderedItem("Unit", number, title);
}

export function formatTopic(number: number, title: string): string {
  return formatOrderedItem("Topic", number, title);
}

export function formatDay(
  number: number,
  title: string,
  displayOrder: boolean = false,
): string {
  return displayOrder ? formatOrderedItem("Day", number, title) : title;
}

const DayBreadcrumbs = ({
  learnerName,
  learners,
  unit,
  topic,
  day,
}: DayBreadcrumbsProps): React.ReactNode => {
  const { courseId, tab, learnerId, dayId, contentAreaId } = useParams<{
    courseId: string;
    tab: string;
    learnerId?: string;
    dayId: string;
    contentAreaId: string;
  }>();

  const navigate = useNavigate();
  const breadcrumbsAreEmpty = unit == null && topic == null && day == null;

  if (breadcrumbsAreEmpty) return null;

  const breadcrumbs = [
    unit && formatUnit(unit.displayNumber, unit.name),
    topic && formatTopic(topic.displayNumber, topic.name),
    day && formatDay(day.displayNumber, day.name, !!unit || !!topic),
  ]
    .filter(Boolean)
    .join(", ");

  const singleOptions = learners?.length === 1;
  const selectedOption = singleOptions
    ? learners[0]
    : learners?.find((o) => o.value == learnerId);

  return (
    <div className="relative flex w-full items-center gap-4 border-b border-b-gray-100 bg-white px-4 py-2.5">
      {learnerName && <span className="font-bold">{learnerName}</span>}
      {learners && (
        <Select
          aria-label="Select learner"
          className="md:!w-56 lg:!w-72"
          defaultValue={selectedOption}
          isDisabled={singleOptions}
          isMulti={false}
          isSearchable={true}
          onChange={(option) => {
            if (option && option?.value && learnerId !== option.value) {
              navigate(
                `/courses/${tab}/${courseId}/ungraded/learners/${option.value}/days/${dayId}/content/${contentAreaId}`,
                { replace: true },
              );
            }
          }}
          options={learners}
          placeholder="Select"
        />
      )}
      <span className="font-semibold text-gray-900">{breadcrumbs}</span>
    </div>
  );
};

export default DayBreadcrumbs;
